import { useCallback, useEffect, useState } from 'react';

/**
 * Reading-progress tracking for the Interview Prep book reader.
 * Persisted client-side only (localStorage) — no backend, no auth required.
 * Shape: { [trackId]: string[] } — array of completed topic ids per track.
 */
const STORAGE_KEY = 'ch_interview_prep_progress_v1';
const PROGRESS_EVENT = 'interview-prep-progress-changed';

const readStore = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // localStorage unavailable (private mode / storage full) — progress just won't persist
  }
  window.dispatchEvent(new Event(PROGRESS_EVENT));
};

export const getCompletedTopicIds = (trackId) => new Set(readStore()[trackId] || []);

export const isTopicCompleted = (trackId, topicId) => getCompletedTopicIds(trackId).has(topicId);

export const setTopicCompleted = (trackId, topicId, completed) => {
  const store = readStore();
  const set = new Set(store[trackId] || []);
  if (completed) {
    set.add(topicId);
  } else {
    set.delete(topicId);
  }
  store[trackId] = Array.from(set);
  writeStore(store);
};

export const getTrackProgress = (trackId, totalTopics) => {
  const completed = getCompletedTopicIds(trackId).size;
  const percent = totalTopics > 0 ? Math.round((completed / totalTopics) * 100) : 0;
  return { completed, total: totalTopics, percent };
};

/** Live-updating set of completed topic ids for one track, plus mutators. */
export const useCompletedTopics = (trackId) => {
  const [completed, setCompleted] = useState(() => getCompletedTopicIds(trackId));

  useEffect(() => {
    const sync = () => setCompleted(getCompletedTopicIds(trackId));
    sync();
    window.addEventListener(PROGRESS_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PROGRESS_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [trackId]);

  const markComplete = useCallback(
    (topicId, value = true) => setTopicCompleted(trackId, topicId, value),
    [trackId],
  );

  const toggle = useCallback(
    (topicId) => setTopicCompleted(trackId, topicId, !isTopicCompleted(trackId, topicId)),
    [trackId],
  );

  return { completed, markComplete, toggle };
};

/** Live-updating { completed, total, percent } for one track. */
export const useTrackProgress = (trackId, totalTopics) => {
  const [progress, setProgress] = useState(() => getTrackProgress(trackId, totalTopics));

  useEffect(() => {
    const sync = () => setProgress(getTrackProgress(trackId, totalTopics));
    sync();
    window.addEventListener(PROGRESS_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PROGRESS_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [trackId, totalTopics]);

  return progress;
};
